import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Renderer2, AfterViewInit } from '@angular/core';
import { trigger, transition, style, animateChild, group, animate, query } from '@angular/animations';
import { Store } from '@ngrx/store';

import { MatDialogRef, MatDialog } from '@angular/material/dialog';

import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Selectors, AppState, Actions } from 'store/root-reducer';
import { State as AuthState } from 'store/reducers/auth.reducers';

import { SelectDomainPopupComponent } from 'modules/core/components/select-domain-popup/select-domain-popup.component';

import { Role, RoleType } from 'modules/shared/models/role.models';
import { Domain } from 'modules/shared/models/domain.models';

import { environment } from 'environments/environment';

@Component({
  selector: 'artemis-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOutMobileSidebar', [
      transition('* <=> *', [
        group([
          query('@fadeInOutMobileSidebarContent', animateChild()),
          query('@fadeInOutMobileSidebarBackground', animateChild())
        ])
      ]),
    ]),
    trigger('fadeInOutMobileSidebarBackground', [
      transition('void => *', [
        style({opacity: 0}),
        animate('300ms ease-in-out', style({opacity: 1}))
      ]),
      transition('* => void', [
        style({opacity: 1}),
        animate('300ms ease-in-out', style({opacity: 0}))
      ]),
    ]),
    trigger('fadeInOutMobileSidebarContent', [
      transition('void => *', [
        style({transform: 'translateX(100%)'}),
        animate('300ms ease-in-out', style({transform: 'translateX(0px)'}))
      ]),
      transition('* => void', [
        style({transform: 'translateX(0px)'}),
        animate('300ms ease-in-out', style({transform: 'translateX(100%)'}))
      ]),
    ]),
  ]
})
export class SidebarComponent implements OnInit, AfterViewInit {

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  profileStore$: Observable<AuthState>;
  currentDomainStore$: Observable<Domain>;
  roles: string;
  onlyAdmin: boolean;
  hasAdmin: boolean;

  sidebarOpened: boolean = false;

  enableHandshakes: boolean = environment['ENABLE_HANDSHAKES'];
  enableOpenCorporates: boolean = environment['ENABLE_OPENCORPORATES'];

  isCurrentThemeLight: boolean = false;

  constructor(private cdRef: ChangeDetectorRef,
              private renderer: Renderer2,
              private store: Store<AppState>,
              private dialog: MatDialog) { }

  ngOnInit() {
    this.profileStore$ = this.store.select(Selectors.auth.getAuth);
    this.currentDomainStore$ = this.store.select(Selectors.domain.getCurrentDomain);
    this.getUserRoles();
  }

  ngAfterViewInit() {
    this.checkCurrentTheme();
  }

  /**
   * Get roles of current user
   * @method getUserRoles
   */
  private getUserRoles(): void {
    this.store
      .select(Selectors.auth.getUserRoles)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((userRoles: Array<Role>) => {
        this.onlyAdmin = !userRoles.some((role: Role) => role.name === RoleType.L1) &&
                         !userRoles.some((role: Role) => role.name === RoleType.L2) &&
                         !userRoles.some((role: Role) => role.name === RoleType.L3);
        this.hasAdmin = userRoles.some((role: Role) => role.name === RoleType.ADMIN) ||
                        userRoles.some((role: Role) => role.name === RoleType.SUPER_ADMIN);
        this.roles = userRoles.map((role: any) => role.name).sort().join(', ');

        this.cdRef.detectChanges();
      });
  }

  /**
   * Check current theme: light/dark
   * @method checkCurrentTheme
   */
  private checkCurrentTheme(): void {
    const currentTheme: string = document.body.className.split(' ').find((value: string) => !!~value.search(/theme_(\w)+/g));

    switch (currentTheme) {
      case 'theme_dark':
        this.isCurrentThemeLight = false;
        break;

      case 'theme_light':
        this.isCurrentThemeLight = true;
        break;
    }

    this.cdRef.detectChanges();
  }

  /**
   * Select domain with modal
   * @method selectDomain
   */
  selectDomain(): void {
    const dialogRef: MatDialogRef<SelectDomainPopupComponent> = this.dialog.open(SelectDomainPopupComponent);
    dialogRef
      .beforeClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (domain: Domain) => {
          if (domain) {
            this.store.dispatch(new Actions.domain.ChangeDomain(domain));
            this.toggleSidebar();
          }
        }
      );
  }

  /**
   * Toggles sidebar
   * @method toggleSidebar
   */
  toggleSidebar(): void {
    this.sidebarOpened = !this.sidebarOpened;
    if (this.sidebarOpened) {
      this.renderer.addClass(document.body, 'no-overflow-y');
    } else {
      this.renderer.removeClass(document.body, 'no-overflow-y');
    }
    this.cdRef.detectChanges();
  }

  /**
   * Logout user and takes him to login page
   * @method logOut
   */
  logOut(): void {
    this.store.dispatch(new Actions.auth.LogOut({ isSessionTimeOut: false }));
  }

  /**
   * Handler for theme toggle value change
   * @method themeChanged
   */
  themeChanged(): void {
    this.isCurrentThemeLight = !this.isCurrentThemeLight;
  }
}
